import { Box, Button, Flex, Icon, Text, useTheme } from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AttributeOptions,
  Category,
  Listing,
  PaginatedListingsResponse,
  RootCategory,
} from 'src/api/v1-api';
import { useAttributeSelector } from 'src/contexts/AttributeSelector';
import { useGlobalSearch } from 'src/contexts/globalSearch';
import { useAuth } from 'src/utils/auth';
import { apiRequest } from 'src/utils/fetchUtils';
import * as Sentry from '@sentry/react';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import { useRouter } from 'next/router';
import { FiPlusCircle } from 'react-icons/fi';
import { LISTINGS_PER_PAGE } from 'src/constants/marketplace';
import { CoverLoader, Pagination } from 'src/components/atoms';
import ListingRow from 'src/components/molecules/listing-row';

interface ListingsListViewProps {
  propListings?: PaginatedListingsResponse;
  fetchUrl: string;
  initialMapFetchUrl?: string;
  requiresAuth?: boolean;
  inventoryDisplay?: string;
  resultsPerPage?: number;
  mapResultsPerPage?: number;
  category?: Category | RootCategory | null;
  propNumDisplayListings?: number;
  userChangedPriceRange?: boolean;
  deactivatedListings?: boolean;
  onMountComplete?: () => void;
  onResponseMetadata?: (metadata: any) => void;
}

const ListingsListView = ({
  propListings,
  fetchUrl,
  userChangedPriceRange,
  requiresAuth = false,
  inventoryDisplay = '4_2_grid',
  resultsPerPage = LISTINGS_PER_PAGE,
  category = null,
  propNumDisplayListings = LISTINGS_PER_PAGE,
  deactivatedListings,
  onMountComplete,
  onResponseMetadata,
}: ListingsListViewProps) => {
  const {
    selectedAttributes,
    priceRange,
    selectedSort,
    page,
    setPage,
    maxPrice,
    featured,
    isInitialized,
  } = useAttributeSelector();
  const { getToken } = useAuth();
  const theme = useTheme();
  const { searchQuery } = useGlobalSearch();
  const { marketplace } = useMarketplaceContext();
  const listingsGridRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const [listings, setListings] = useState<Listing[]>([]);
  const [displayedListings, setDisplayedListings] = useState<Listing[]>([]);
  const [rerouting, setRerouting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [haveFetched, setHaveFetched] = useState<boolean>(false);
  const [usedPropListings, setUsedPropListings] = useState<boolean>(false);
  const [nextLink, setNextLink] = useState<string | null>(null);
  const [prevLink, setPrevLink] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [numDisplayListings, setNumDisplayListings] = useState<number>(propNumDisplayListings);

  // Load ISR pre-fetched listings on initial render when no filters are applied
  // This effect includes ALL dependencies to avoid stale closures, but guards prevent re-execution
  useEffect(() => {
    // Guard: Only execute once when conditions are right
    if (haveFetched || usedPropListings) return;

    const shouldUse =
      !!propListings?.results &&
      isInitialized &&
      page === 1 &&
      !Object.keys(selectedAttributes).length &&
      priceRange[0] === 0 &&
      (priceRange[1] === 0 || priceRange[1] === maxPrice) &&
      !selectedSort &&
      !searchQuery &&
      !featured;

    if (shouldUse) {
      setListings(propListings.results);
      setNextLink(propListings.next);
      setPrevLink(propListings.previous);
      setTotalPages(propListings.count / resultsPerPage);
      setPage(propListings.page_number);
      setHaveFetched(true);
      setUsedPropListings(true);
    }
  }, [
    propListings,
    isInitialized,
    page,
    selectedAttributes,
    priceRange,
    maxPrice,
    selectedSort,
    searchQuery,
    featured,
    haveFetched,
    usedPropListings,
    resultsPerPage,
  ]);

  const fetchListings = useCallback(
    async (
      url: string,
      filters: AttributeOptions = {},
      priceRange: [number, number],
      page: number,
    ) => {
      if (!loading && page > 0) {
        setLoading(true);
        const params = new URLSearchParams();

        // If there's a price range and the user has set a value, add it to the params
        if (priceRange[0] !== 0 || (priceRange[1] !== 0 && priceRange[1] != maxPrice)) {
          params.append('price', `${priceRange[0]}-${priceRange[1]}`);
        }

        // If featured listings are requested, add it to the params
        if (featured) {
          params.append('is_featured', 'true');
        }

        // Convert filters object to query parameters
        for (const [key, values] of Object.entries(filters)) {
          if (key === 'page' && url.includes('page')) {
            continue;
          }
          for (const value of values) {
            if (value.toString().length > 0) {
              params.append(key, value.toString());
            }
          }
        }
        const paramsString = params.toString();
        const fullUrl = paramsString ? `${url}&${paramsString}` : url;

        try {
          let token = null;
          if (requiresAuth) {
            token = await getToken();
          }

          const response = await apiRequest('GET', fullUrl, token);
          setListings(response.results);
          setNextLink(response.next);
          setPrevLink(response.previous);
          setTotalPages(response.count / resultsPerPage);
          setPage(response.page_number);
          setHaveFetched(true);

          if (onResponseMetadata) {
            onResponseMetadata(response);
          }
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              component: 'ListingsListView',
              marketplaceId: marketplace?.id,
              categoryId: category?.id,
              page: page,
              params: paramsString,
              url: fullUrl,
            },
          });
        } finally {
          setLoading(false);
        }
        if (userChangedPriceRange || searchQuery != '') {
          listingsGridRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getToken,
      loading,
      requiresAuth,
      userChangedPriceRange,
      searchQuery,
      featured,
      maxPrice,
      onResponseMetadata,
    ],
  );

  useEffect(() => {
    // We need to avoid loading when page is 0. This means that the app is
    // currently loading and waiting the page to be updated from query string.
    // After this is set to page = 1 or page = query params page, it should
    // request the data from the api.
    if (page === 0) {
      return;
    }
    setRerouting(false);

    const url = `${fetchUrl}/?page_size=${resultsPerPage}&page=${page}${
      selectedSort ? `&sort=${selectedSort}` : ''
    }${searchQuery ? `&search=${searchQuery}` : ''}`;

    const shouldFetchListings =
      !url.includes('undefined') &&
      inventoryDisplay !== 'None' &&
      !usedPropListings &&
      !haveFetched;

    if (shouldFetchListings) {
      fetchListings(url, selectedAttributes, priceRange, page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // selectedListings,
    selectedAttributes,
    priceRange,
    fetchUrl,
    page,
    selectedSort,
    haveFetched,
    usedPropListings,
    searchQuery,
    featured,
  ]);

  useEffect(() => {
    // Reset the page to 1 when the filters change
    setPage(1);
    setHaveFetched(false);
  }, [selectedAttributes, priceRange, selectedSort, searchQuery, featured]);

  useEffect(() => {
    setDisplayedListings(listings.slice(0, numDisplayListings));

    // If the number of displayed listings is less than LISTINGS_PER_PAGE,
    // and the next link is not null, trigger a Sentry event
    if (numDisplayListings < LISTINGS_PER_PAGE && nextLink) {
      Sentry.captureEvent({
        message: 'ListingsGrid: Number of displayed listings is less than LISTINGS_PER_PAGE',
        extra: {
          nextLink,
          categoryId: category?.id,
          marketplaceId: marketplace?.id,
          page,
          numDisplayListings,
        },
      });
    }
  }, [listings, numDisplayListings]);

  const updateQueryParamAndContextPage = (link?: string | null, calculatedPage?: number) => {
    let pageNumber = '';

    if (link) {
      try {
        pageNumber = new URL(link).searchParams.get('page') ?? '1';
      } catch (error) {
        pageNumber = link.split('page=')[1];
      }
    } else if (calculatedPage) {
      pageNumber = calculatedPage.toString();
    } else {
      return;
    }

    let pageQueryName = 'page';
    if (deactivatedListings) {
      pageQueryName = 'deactivated_listings_page';
    }
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set(pageQueryName, pageNumber);
    setPage(parseInt(pageNumber));
    const searchParams = new URLSearchParams();
    currentUrl.searchParams.forEach((value, key) => {
      searchParams.append(key, value);
    });
    const newUrl = `${currentUrl.origin}${currentUrl.pathname}?${searchParams.toString()}`;
    window.history.replaceState({ ...window.history.state, as: newUrl, url: newUrl }, '', newUrl);
    setHaveFetched(false);
  };

  const handleNextPage = () => {
    const totalPageCount = Math.ceil(totalPages ?? 0);
    const hasNext = nextLink !== null || (page < totalPageCount && totalPages !== null);

    if (hasNext) {
      setRerouting(true);
      if (nextLink) {
        updateQueryParamAndContextPage(nextLink);
      } else {
        // Calculate next page when link is not available
        updateQueryParamAndContextPage(null, page + 1);
      }
      listingsGridRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handlePrevPage = () => {
    const hasPrev = prevLink !== null || (page > 1 && totalPages !== null);

    if (hasPrev) {
      setRerouting(true);
      if (prevLink) {
        updateQueryParamAndContextPage(prevLink);
      } else {
        // Calculate previous page when link is not available
        updateQueryParamAndContextPage(null, page - 1);
      }
      listingsGridRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (onMountComplete && haveFetched) {
      onMountComplete();
    }
  }, [onMountComplete, haveFetched]);

  return (
    <Flex direction="column" width="100%" gap={4} pb={8} data-test="listing-view">
      <Box ref={listingsGridRef} position="relative">
        {!loading && haveFetched && listings && listings.length === 0 && (
          <Box
            gridColumn="span 4/span 4"
            textAlign="center"
            p={8}
            mt={4}
            border="1px solid"
            borderRadius={theme.border_radius.border_radius_2}
            borderColor="gray.300"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Text fontSize="lg" color="gray">
              No listings yet
            </Text>
            {category?.functional?.show_create_listing_button && (
              <Button
                mt={8}
                colorScheme="primary"
                onClick={() => router.push(`/listing/create?category=${category?.id}`)}
              >
                <Icon as={FiPlusCircle} mr="2" />
                {category?.functional?.text_overrides?.add_listing_btn || 'Add Listing'}
              </Button>
            )}
          </Box>
        )}
      </Box>
      {displayedListings.length > 0 &&
        displayedListings.map((listing) => (
          <ListingRow key={listing.id} listing={listing} category={category} loading={loading} />
        ))}
      {(loading || rerouting) && <CoverLoader py={8} context="ListingsListView:LoadingListings" />}
      <Pagination
        nextLink={nextLink}
        prevLink={prevLink}
        onNext={handleNextPage}
        onPrev={handlePrevPage}
        pageNumber={page.toString()}
        totalPages={totalPages}
      />
    </Flex>
  );
};

export default ListingsListView;
